import { memo, useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { Loader, cn, useMedias } from '@divlab/divanui';
import { useDebouncedCallback } from 'use-debounce';
import { CSSTransition } from 'react-transition-group';

import CustomYMaps from '@Components/CustomYMaps';
import PopupAllSellPoints from '../PopupAllSellPoints';
import PopupSelectedSellPoint from '../PopupSelectedSellPoint';
import useSellPointsSearch from '@Queries/useSellPointsSearch';
import useSelectedSellPoint from '@Queries/useSelectedSellPoint';
import HideMapButton from './elements/HideMapButton';
import DraggableDrawer from '@Components/DraggableDrawer';
import styles from './MapWithPopup.module.css';

import type { FC, HTMLAttributes } from 'react';
import type { SellPointData } from '@Types/SellPoints';
import type { MapData } from '@Types/Map';
import type { SellPointDataSearch } from '@Types/SellPointsSearch';
import type { MapInstance } from '@Components/CustomYMaps/elems/CustomMap';

export interface MapWithPopupProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  map?: MapData;
  defaultSelectedPoint?: SellPointData;
  pickupPoint?: boolean;
}

const MapWithPopup: FC<MapWithPopupProps> = (props) => {
  const { className, map, defaultSelectedPoint, pickupPoint, ...restProps } = props;
  const [visiblePopup, setVisiblePopup] = useState(true);
  const [selectedPoint, setSelectedPoint] = useState<SellPointData>(defaultSelectedPoint);
  const [isPageScrolling, setIsPageScrolling] = useState(false);
  const { isMobile } = useMedias();
  const containerRef = useRef<HTMLDivElement>();
  const [mapHeight, setMapHeight] = useState(containerRef.current?.clientHeight);
  const mapInstance = useRef<MapInstance>();

  const [searchParamsValue, setSearchParamsValue] = useState('');

  const onChangeParams = useDebouncedCallback((str: string) => {
    setSearchParamsValue(str);
  }, 200);

  const { data: searchedSellPoints } = useSellPointsSearch({
    params: searchParamsValue,
    pickupPoint,
  });
  const { data: selectedSellPoint, isFetching } = useSelectedSellPoint(selectedPoint?.id);

  const [mapCoordinates, setMapCoordinates] = useState<number[][]>([]);

  const onBoundsChange = (number: number[][]) => {
    setMapCoordinates(number);
  };

  const sellPoints: SellPointDataSearch[] = useMemo(() => {
    if (!mapCoordinates.length || !searchedSellPoints) return [];
    return searchedSellPoints?.filter((sellPoint) => {
      const sellPointLatitude = sellPoint.coordinates[0];
      const sellPointLongitude = sellPoint.coordinates[1];

      const mapLatitudeStart = mapCoordinates[0][0];
      const mapLatitudeEnd = mapCoordinates[1][0];
      const mapLongitudeStart = mapCoordinates[0][1];
      const mapLongitudeEnd = mapCoordinates[1][1];

      return (
        sellPointLatitude >= mapLatitudeStart &&
        sellPointLongitude >= mapLongitudeStart &&
        sellPointLatitude <= mapLatitudeEnd &&
        sellPointLongitude <= mapLongitudeEnd
      );
    });
  }, [searchedSellPoints, mapCoordinates]);

  const handleSelectPoint = useCallback((_e, selectedPickupPoint) => {
    setSelectedPoint(selectedPickupPoint);
    setVisiblePopup(true);
  }, []);

  const handleClose = useCallback(() => {
    setVisiblePopup(false);
    setSelectedPoint(null);
  }, []);

  const handleMore = useCallback((_e, sellPoint) => {
    setSelectedPoint(sellPoint);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedPoint(null);
  }, []);

  const handleListVisible = () => {
    setVisiblePopup((prev) => {
      if (!prev) {
        setSelectedPoint(null);
      }
      return !prev;
    });
  };

  const handleHeightChange = useCallback((size) => {
    setMapHeight(containerRef.current?.clientHeight - size);
  }, []);

  useEffect(() => {
    setSelectedPoint(defaultSelectedPoint);
  }, [defaultSelectedPoint]);

  useEffect(() => {
    if (mapInstance.current) {
      mapInstance.current.container.fitToViewport();
    }
  }, [mapHeight]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const handleScroll = () => {
      setIsPageScrolling(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setIsPageScrolling(false), 200);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div {...restProps} ref={containerRef} className={cn(styles.mapWithPopup, className)}>
      <div
        className={cn(styles.map, { [styles.fullWidth]: !visiblePopup })}
        style={{ height: isMobile ? mapHeight : '100%' }}
      >
        <CustomYMaps
          map={map}
          resized={visiblePopup}
          needZoom
          needScrollZoom={!isPageScrolling}
          needGeolocation
          getMapInstance={(instance) => (mapInstance.current = instance)}
          sellPoints={sellPoints}
          selectedPoint={selectedPoint}
          onSelectPickupPoint={handleSelectPoint}
          onBoundsChange={onBoundsChange}
          hideFullscreen
        />
        {(!visiblePopup || !isMobile) && (
          <HideMapButton
            open={visiblePopup}
            className={!visiblePopup ? styles.visibleImage : styles.hiddenImage}
            onClick={handleListVisible}
          />
        )}
      </div>

      {visiblePopup && (
        <DraggableDrawer
          onHeightChange={handleHeightChange}
          onHide={handleClose}
          initialHeight={selectedSellPoint ? '95%' : '50%'}
          containerRef={containerRef}
          disabled={!isMobile}
          className={styles.drawer}
        >
          <div className={styles.popup}>
            <CSSTransition
              in={!!selectedSellPoint}
              timeout={500}
              unmountOnExit
              classNames={{
                enter: styles.slideInEnter,
                enterActive: styles.slideInEnterActive,
                exit: styles.slideInExit,
                exitActive: styles.slideInExitActive,
              }}
            >
              <div className={styles.selectedSellPointContainer}>
                {selectedSellPoint && (
                  <PopupSelectedSellPoint
                    selectedPoint={selectedSellPoint}
                    onBack={handleBack}
                    onChangeParams={onChangeParams}
                  />
                )}
              </div>
            </CSSTransition>
            {selectedPoint && isFetching && (
              <div className={styles.loader}>
                <Loader theme='dark' size='huge' />
              </div>
            )}
            {!selectedSellPoint && (
              <PopupAllSellPoints
                sellPoints={sellPoints}
                onMore={handleMore}
                onChangeParams={onChangeParams}
                searchParamsValue={searchParamsValue}
                infoText={map?.nearestShowroomTitle}
              />
            )}
          </div>
        </DraggableDrawer>
      )}
    </div>
  );
};

export default memo(MapWithPopup);
